<template>
  <div>
    <h3
      v-if="visible && heading"
      class="header"
    >
      {{ $t(`message.form.headings.${heading}`) }}
    </h3>
    <component
      :is="component"
      v-if="visible"
      :id="`form-field-${scope}-${name}`"
      ref="field"
      :disabled="disabled"
      :scope="scope"
      :label="label"
      :name="name"
      :validation="computedValidation"
      :form-value="formValue"
      :value="value"
      v-bind="{ ...$attrs, ...params }"
      :required-for-publish="computedRequiredForPublish"
      :required-for-publish-message="requiredForPublishMessage"
      :authorized-to-be-edited="userHasPermissionToEditField"
      @request-population="$emit('request-population', $event)"
      @bcube-assistant="$emit('bcube-assistant', $event)"
    />
    <nx-dialog
      v-if="hasDisclaimers"
      :value="disclaimerActive"
      max-width="400px"
      icon="warning"
      icon-color="orange"
      :title="$t('message.form.disclaimer.title')"
      @input="closeDisclaimer"
    >
      <p
        v-if="disclaimerMessage"
        v-html="$t(`message.form.disclaimer.message.${disclaimerMessage}`)"
      />
      <template slot="actions">
        <v-btn
          color="secondary"
          flat
          @click="closeDisclaimer"
        >
          {{ $t('message.common.accept') }}
        </v-btn>
      </template>
    </nx-dialog>
  </div>
</template>
<script>
import Vue from 'vue';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import { difference } from '@/utils';
import ShowDisableConditionally from '@/modules/form/mixins/show-disable-conditionally';
import NxDialog from '@/components/dialogs/Dialog';
import authorizable from '@/modules/authorization/mixins/authorizable';
import fastEqual from 'fast-deep-equal';

export default {
  name: 'NxFormScopeFieldGenerator',
  components: {
    NxDialog,
  },
  mixins: [ShowDisableConditionally, authorizable],
  props: {
    component: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: false,
    },
    entity: {
      type: String,
      required: false,
    },
    scope: {
      type: String,
    },
    label: {
      type: String,
    },
    validation: {
      type: Object,
      default: () => ({}),
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: undefined,
    },
    formValue: {
      type: Object,
    },
    disableIf: {
      type: Object,
    },
    disableIfOverwriteValueWith: {
      type: undefined,
    },
    requiredIf: {
      type: String,
    },
    requiredForPublish: {
      type: Boolean,
    },
    requiredForPublishIf: {
      type: String,
    },
    requiredIfEmpty: {
      type: String,
    },
    requiredForPublishIfEmpty: {
      type: String,
    },
    requiredForPublishMessage: {
      type: String,
    },
    notRequiredIf: {
      type: String,
    },
    notRequiredIfEmpty: {
      type: String,
    },
    requiredIfAny: {
      type: Array,
      default: () => ([]),
    },
    requiredIfAll: {
      type: Array,
      default: () => ([]),
    },
    emptyIf: {
      type: String,
    },
    heading: {
      type: String,
    },
    valueToDisclaimerMessage: {
      type: Object,
      default: () => ({}),
    },
    disclaimerOnModified: {
      type: String,
      default: () => (''),
    },
    triggerPopulation: {
      type: Boolean,
    },
    usePopulateFieldsCmsApiService: {
      type: Boolean,
    },
    disclaimerIgnoreFieldId: Boolean,
    enableIfPermissionAction: {
      type: String,
      required: false,
      default: null,
    },
    enableIfPermissionActionEntity: {
      type: String,
      required: false,
      default: null,
    },
    enableIfPermissionActionCurrentValue: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      disclaimerMessage: null,
      disclaimerActive: false,
      disclaimerAlreadyShown: false,
      userHasPermissionToEditField: true,
    };
  },
  computed: {
    disabled() {
      // Calculated on mounted
      if (this.userHasPermissionToEditField === false) {
        return true;
      }

      return this.isDisabled({ params: this.params });
    },
    visible() {
      return this.isVisible({ params: this.params });
    },
    computedRequiredForPublish() {
      if (this.requiredForPublish) {
        return this.requiredForPublish;
      }
      if (isEmpty(this.formValue)) {
        return false;
      }
      if (this.requiredForPublishIf) {
        return !!this.formValue[this.requiredForPublishIf];
      }
      if (this.requiredForPublishIfEmpty) {
        if (isArray(this.formValue[this.requiredForPublishIfEmpty])) {
          return this.formValue[this.requiredForPublishIfEmpty].length === 0;
        }
        return !this.formValue[this.requiredForPublishIfEmpty];
      }
      if (this.requiredForPublishIf) {
        return !!this.formValue[this.requiredForPublishIf];
      }
      return false;
    },
    computedValidation() {
      const validation = Vue.observable({
        required: false,
        requiredForPublish: false,
        ...this.validation,
      });
      if (!isObject(this.formValue)) {
        return validation;
      }
      if (this.requiredIf) {
        validation.required = !!this.formValue[this.requiredIf];
      }
      if (this.requiredIfEmpty) {
        validation.required = !this.formValue[this.requiredIfEmpty];
      }
      if (this.requiredIfAny.length > 0) {
        validation.required = !!this.requiredIfAny.some(field => !!this.formValue[field]);
      }
      if (this.requiredIfAll.length > 0) {
        validation.required = this.requiredIfAny.every(field => !!this.formValue[field]);
      }
      if (this.notRequiredIf && this.formValue[this.notRequiredIf]) {
        validation.required = false;
      }
      if (this.notRequiredIfEmpty && !this.formValue[this.notRequiredIfEmpty]) {
        validation.required = false;
      }
      if (this.emptyIf) {
        validation.empty_if = this.formValue[this.emptyIf];
      }
      return validation;
    },
    hasDisclaimers() {
      return !isEmpty(this.valueToDisclaimerMessage) || this.disclaimerOnModified;
    },
    canTriggerPopulateFields() {
      return this.triggerPopulation && this.usePopulateFieldsCmsApiService;
    },
  },
  watch: {
    computedValidation(newValue, oldValue) {
      if (get(this.$refs.field, 'flags.valid') === false
        && !fastEqual(newValue, oldValue)
      ) {
        this.$nextTick(this.$refs.field.validate);
      }
    },
    disabled(newVal, oldVal) {
      if (this.params.disabled === true) {
        return;
      }
      // overwrite the field value on disableIf user interaction
      if (oldVal === false
        && newVal === true
        && this.disableIf
        && typeof this.disableIfOverwriteValueWith !== 'undefined') {
        this.$refs.field.onDefault(this.disableIfOverwriteValueWith);
        this.$nextTick(this.$refs.field.validate);
      }
    },
    value(newVal, oldVal) {
      const newValIsObject = isObject(newVal) && typeof newVal.id !== 'undefined';
      const oldValIsObject = isObject(oldVal) && typeof oldVal.id !== 'undefined';

      const sameObjectId = newValIsObject
        && (
          (
            // Both objects cards
            oldValIsObject
            && newVal.id === oldVal.id
          ) || (
            // Id to object card
            !oldValIsObject
            && newVal.id === oldVal
          )
        );

      if (this.canTriggerPopulateFields && !sameObjectId) {
        this.$emit('trigger-population', { field: this.name, value: newVal });
      }
      if (!this.hasDisclaimers) {
        return;
      }
      if (this.disclaimerAlreadyShown) {
        return;
      }
      // undefined vs null
      if (isNil(newVal) && isNil(oldVal)) {
        return;
      }

      // object could be different if contains modificationDates
      if (!this.disclaimerIgnoreFieldId && sameObjectId) {
        return;
      }

      // ODMPPHE: En el caso de taxonUrl y boardUrl, el valor siempre cambia cuando modificas su asociación con portada
      // o comportamiento manual/auto porque API borra y crear un nuevo taxonUrl/boardUrl
      // Por lo tanto, tenemos que comprobar que el valor "path" no se ha modificado, que al final es lo que importa
      // @see: https://mgr.bitban.com/issues/91495
      const urlTypeFields = ['taxonUrl', 'boardUrl'];
      let valuesAreEqual = false;
      if (newValIsObject
        && oldValIsObject
        && urlTypeFields.includes(newVal.type)
        && urlTypeFields.includes(oldVal.type)
      ) {
        valuesAreEqual = isEqual(newVal.path, oldVal.path);
      } else {
        valuesAreEqual = newValIsObject && oldValIsObject
          ? isEmpty(difference(newVal, oldVal))
          : isEqual(newVal, oldVal);
      }

      if (valuesAreEqual) {
        return;
      }

      // Api transforms true/false values to 1/0
      let value = newVal;
      if (typeof value === 'boolean') {
        value = Number(value);
      }
      if (this.valueToDisclaimerMessage[value] !== undefined) {
        this.disclaimerMessage = this.valueToDisclaimerMessage[value];
        this.disclaimerActive = true;
        this.disclaimerAlreadyShown = true; // Once per field
      } else if (this.disclaimerOnModified) {
        this.disclaimerMessage = this.disclaimerOnModified;
        this.disclaimerActive = true;
        this.disclaimerAlreadyShown = true; // Once per field
      }
    },
  },
  async mounted() {
    if (this.enableIfPermissionAction) {
      // if current value is established only check permission if matches
      if (this.enableIfPermissionActionCurrentValue) {
        if (this.compareWithOperator(
          this.value,
          this.enableIfPermissionActionCurrentValue.value,
          this.enableIfPermissionActionCurrentValue.operator,
        )
        ) {
          await this.checkUserHasPermissionToEditField();
        }
      } else {
        await this.checkUserHasPermissionToEditField();
      }
    }

    if (!this.hasDisclaimers) {
      return;
    }
    // After entity save, restore disclaimer shown value to allow show it again
    this.$store.subscribe((mutation) => {
      if (!mutation.type.includes('SET_CURRENT_CONTENT')
        && !mutation.type.includes('SET_ENTITY_DATA')) {
        return;
      }
      this.$nextTick(() => {
        this.disclaimerAlreadyShown = false;
      });
    });
  },
  methods: {
    closeDisclaimer() {
      this.disclaimerActive = false;
    },
    async checkUserHasPermissionToEditField() {
      this.userHasPermissionToEditField = await this.can(
        `${this.enableIfPermissionActionEntity || this.entity}.${this.enableIfPermissionAction}`,
        {
          entity: this.enableIfPermissionActionEntity || this.entity,
          id: this.entityId,
        },
      );
    },
  },
};
</script>
<style lang="stylus" scoped>
@import '../../assets/stylus/variables.styl'
.header {
  color: $nx-primary--darken-2;
  font-size: 15px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.02em;
  margin-top: 4px;
}
</style>
